// src/services/approval-flows.service.ts

import apiClient from '@/services/api/client';

export type ChainStepStatus = 'waiting' | 'pending' | 'approved' | 'rejected';

export interface ChainStep {
  order: number;
  approverUid: string;
  approverName: string;
  status: ChainStepStatus;
  decidedAt?: string;
  comment?: string;
}

export type ApprovalFlowStatus =
  | 'pending_responsable'
  | 'in_chain'
  | 'approved'
  | 'rejected'
  | 'rejected_to_responsable';

export interface ApprovalFlow {
  id: string;
  documentId: string;
  documentName: string;
  documentCode: string;
  documentVersion: string;
  fileUrl?: string;
  previousVersion?: string;
  correctionId: string;
  correctionNote: string;
  requestedByUid: string;
  requestedByName: string;
  responsableUid: string;
  responsableName: string;
  chain: ChainStep[];
  currentStep: number;
  status: ApprovalFlowStatus;
  rejectionNote?: string;
  rejectedByUid?: string;
  rejectedByName?: string;
  rejectedByStep?: number;
  rejectedAt?: string;
  createdAt: string;
  updatedAt: string;
}

class ApprovalFlowsService {
  async createFlow(
    data: Omit<ApprovalFlow, 'id' | 'createdAt' | 'updatedAt'>,
  ): Promise<string> {
    const response = await apiClient.post('/approval-flows', data);
    return response.data.data.id as string;
  }

  async updateFlow(id: string, updates: Partial<ApprovalFlow>): Promise<void> {
    await apiClient.put(`/approval-flows/${id}`, updates);
  }

  async getFlowsForResponsable(uid: string): Promise<ApprovalFlow[]> {
    const response = await apiClient.get('/approval-flows', {
      params: { responsableUid: uid }
    });
    return response.data.data as ApprovalFlow[];
  }

  onFlowsForResponsable(uid: string, cb: (flows: ApprovalFlow[]) => void, intervalMs: number = 15000): () => void {
    let active = true;
    let timer: number | undefined;

    const fetchItems = async () => {
      if (!active) return;
      try {
        const flows = await this.getFlowsForResponsable(uid);
        cb(flows);
      } catch (err) {
        console.error('[approvalFlows] polling error:', err);
      } finally {
        if (active) timer = window.setTimeout(fetchItems, intervalMs);
      }
    };

    fetchItems();
    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
    };
  }

  onPendingFlowsForApprover(uid: string, cb: (flows: ApprovalFlow[]) => void, intervalMs: number = 15000): () => void {
    let active = true;
    let timer: number | undefined;

    const fetchItems = async () => {
      if (!active) return;
      try {
        const response = await apiClient.get('/approval-flows', {
          params: { approverUid: uid, status: 'in_chain' }
        });
        cb(response.data.data as ApprovalFlow[]);
      } catch (err) {
        console.error('[approvalFlows] polling error:', err);
      } finally {
        if (active) timer = window.setTimeout(fetchItems, intervalMs);
      }
    };

    fetchItems();
    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
    };
  }

  async submitApproverDecision(
    flowId: string,
    stepIndex: number,
    approved: boolean,
    comment: string,
    documentId: string,
  ): Promise<void> {
    await apiClient.post(`/approval-flows/${flowId}/decision`, {
      stepIndex,
      approved,
      comment,
      documentId
    });
  }

  async assignChain(
    flowId: string,
    newChain: Omit<ChainStep, 'status' | 'decidedAt' | 'comment'>[],
    documentId: string,
  ): Promise<void> {
    await apiClient.post(`/approval-flows/${flowId}/assign-chain`, {
      chain: newChain,
      documentId
    });
  }

  async directApprove(flowId: string, documentId: string): Promise<void> {
    await apiClient.post(`/approval-flows/${flowId}/direct-approve`, {
      documentId
    });
  }

  async directReject(
    flowId: string,
    documentId: string,
    note: string,
  ): Promise<void> {
    await apiClient.post(`/approval-flows/${flowId}/direct-reject`, {
      documentId,
      note
    });
  }
}

export const approvalFlowsService = new ApprovalFlowsService();
